;(function () {
  'use strict';

  // Shared vertical overlay scrollbar for drawer scroll bodies.
  // Binds to every [data-drawer-scroll] element and injects an inline-styled
  // overlay bar (a track line + a purple thumb) into the scroll body's PARENT —
  // NOT the scroll body itself. The bar is position:absolute over the body's box,
  // so:
  //   • it never lives inside the scroll container → never inflates scrollHeight
  //     (no phantom blank space in any engine);
  //   • the track is composited by the browser and never transformed per-scroll →
  //     it stays put as smoothly as a native scrollbar; only the thumb tracks scroll.
  //
  // Injected DOM uses inline styles referencing CSS custom properties only —
  // NO Tailwind class strings (JS-only class strings are not scanned by Tailwind).
  //
  // Side grammar (data-drawer-scroll value): base side ('left' | default 'right')
  // plus an optional 'lg:left'/'lg:right' override at --breakpoint-lg (1024px), for
  // drawers whose anchored side flips across breakpoints (e.g. wishlist/mini-bag:
  // left on mobile, right on lg+).

  var registry = new Map(); // scrollEl -> { teardown }

  function build(scrollEl) {
    if (registry.has(scrollEl)) return; // idempotent — never inject twice
    var parent = scrollEl.parentNode;
    if (!parent || parent.nodeType !== 1) return;
    // Positioning context for the absolute bar — enforce without touching markup.
    if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';

    // Keyboard operability (WCAG 2.1.1): a plain overflow div isn't focusable, so
    // arrow keys only scroll it after a wheel gesture makes it the browser's implicit
    // scroll target. Make it focusable so a click or Tab targets it immediately — and
    // so keyboard-only users can scroll bodies with no focusable content (size guide).
    var addedTabindex = !scrollEl.hasAttribute('tabindex');
    if (addedTabindex) scrollEl.setAttribute('tabindex', '0');

    var attr = (scrollEl.getAttribute('data-drawer-scroll') || '').trim();
    var baseSide = attr.split(/\s+/)[0] === 'left' ? 'left' : 'right';
    var lgOverride = (attr.match(/lg:(left|right)/) || [])[1];
    var lgMq = lgOverride ? window.matchMedia('(min-width: 1024px)') : null;
    function currentSide() { return (lgMq && lgMq.matches) ? lgOverride : baseSide; }

    // Bar / grab zone — lives in the parent, placed over the scroll body's box by
    // place(). width = the invisible hit-area; z-index above scroll-body content
    // (cards paint internals up to z-60).
    var bar = document.createElement('div');
    bar.setAttribute('data-drawer-scrollbar', '');
    bar.setAttribute('aria-hidden', 'true'); // non-focusable, invisible to AT + focus-trap
    bar.style.cssText = 'position:absolute;width:var(--spacing-xl);z-index:70;display:none;touch-action:none;';
    // Visible track line (--spacing-2xs = 4px).
    var line = document.createElement('div');
    line.style.cssText = 'position:absolute;top:0;bottom:0;width:var(--spacing-2xs);background:var(--color-rule-tertiary);';
    // Thumb (--spacing-2xs = 4px, purple).
    var thumb = document.createElement('div');
    thumb.setAttribute('data-drawer-scrollbar-thumb', '');
    thumb.style.cssText = 'position:absolute;width:var(--spacing-2xs);background:var(--color-purple);';
    bar.appendChild(line);
    bar.appendChild(thumb);
    parent.appendChild(bar);

    // Touch-primary devices: the grab zone overlaps controls pinned to the body's edge (mini-bag
    // remove/wishlist icons) and would swallow their taps, while finger scrolling is native anyway.
    // Let taps fall through and keep the bar as a visual indicator only (TASK-57291).
    var coarseMq = window.matchMedia('(pointer: coarse)');
    function applyPointerMode() { bar.style.pointerEvents = coarseMq.matches ? 'none' : ''; }
    applyPointerMode();
    coarseMq.addEventListener('change', applyPointerMode);

    // Overlay the bar exactly onto the scroll body's box within the positioned parent.
    function place() {
      var pr = parent.getBoundingClientRect();
      var br = scrollEl.getBoundingClientRect();
      bar.style.top = (br.top - pr.top) + 'px';
      bar.style.height = br.height + 'px';
      if (currentSide() === 'left') {
        bar.style.right = '';
        bar.style.left = Math.max(0, br.left - pr.left) + 'px';
      } else {
        bar.style.left = '';
        bar.style.right = Math.max(0, pr.right - br.right) + 'px';
      }
    }

    // line + thumb hug the current side within the bar; then re-place to that edge.
    function applySide() {
      var s = currentSide(), other = s === 'left' ? 'right' : 'left';
      line.style[other] = ''; line.style[s] = '0';
      thumb.style[other] = ''; thumb.style[s] = '0';
      place();
    }

    // Scroll: only the thumb moves — the track is fixed, so this stays smooth.
    function update() {
      var sh = scrollEl.scrollHeight, ch = scrollEl.clientHeight;
      if (sh - ch <= 1) { bar.style.display = 'none'; return; }
      bar.style.display = 'block';
      thumb.style.height = (ch / sh * 100) + '%';
      thumb.style.top = (scrollEl.scrollTop / sh * 100) + '%';
    }

    function refresh() { place(); update(); }

    scrollEl.addEventListener('scroll', update, { passive: true });

    // Drag: map pointer Y within the grab zone to scrollTop. No wheel handler —
    // vertical scroll is native. The bar's rect is stable (fixed in the parent).
    // Click on the line outside the thumb jumps; clicking the thumb grabs. Pointer
    // capture keeps the drag alive off the thin bar.
    bar.addEventListener('pointerdown', function (e) {
      var max = scrollEl.scrollHeight - scrollEl.clientHeight;
      if (max <= 1) return;
      e.preventDefault();
      var rect = bar.getBoundingClientRect();
      var tr = thumb.getBoundingClientRect();
      if (e.clientY < tr.top || e.clientY > tr.bottom) {
        scrollEl.scrollTop = Math.min(max, Math.max(0, (e.clientY - rect.top) / rect.height * max));
      }
      var startY = e.clientY, startScroll = scrollEl.scrollTop;
      var factor = scrollEl.scrollHeight / rect.height;
      var root = document.documentElement;
      root.setAttribute('data-drawer-dragging', ''); // global grabbing cursor (unlayered CSS)
      bar.style.cursor = 'grabbing';
      try { bar.setPointerCapture(e.pointerId); } catch (_) {}
      function move(ev) {
        scrollEl.scrollTop = Math.min(max, Math.max(0, startScroll + (ev.clientY - startY) * factor));
      }
      function up(ev) {
        root.removeAttribute('data-drawer-dragging');
        bar.style.cursor = '';
        try { bar.releasePointerCapture(ev.pointerId); } catch (_) {}
        bar.removeEventListener('pointermove', move);
        bar.removeEventListener('pointerup', up);
        bar.removeEventListener('pointercancel', up);
        bar.removeEventListener('lostpointercapture', up);
      }
      bar.addEventListener('pointermove', move);
      bar.addEventListener('pointerup', up);
      bar.addEventListener('pointercancel', up); // OS/browser took the gesture → tear down (else drag sticks)
      bar.addEventListener('lostpointercapture', up);
    });

    // Cursor hint on desktop: grab over the thumb, default over the line.
    bar.addEventListener('mousemove', function (e) {
      if (document.documentElement.hasAttribute('data-drawer-dragging')) return;
      var tr = thumb.getBoundingClientRect();
      bar.style.cursor = (e.clientY >= tr.top && e.clientY <= tr.bottom) ? 'grab' : '';
    });

    // Coalesce bursts of mutations/resizes into one measure per frame (accordions,
    // hover class toggles and content re-renders can fire many at once).
    var rafId = 0;
    function scheduleRefresh() {
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = 0;
        if (!parent.contains(bar)) parent.appendChild(bar); // parent wiped → re-append
        refresh();
      });
    }

    // Re-place + re-measure on size change (viewport, panel/header/footer height,
    // drawer open) and on async content (cart items, Voyado facets, results). The
    // bar lives in the parent, so a scroll-body innerHTML replace can't detach it.
    var ro = (typeof ResizeObserver === 'function') ? new ResizeObserver(scheduleRefresh) : null;
    if (ro) { ro.observe(scrollEl); ro.observe(parent); } else window.addEventListener('resize', scheduleRefresh);
    // childList → content re-renders. attributes (semantic collapse states only, to
    // avoid re-measuring on hover class/style churn) → <details>/accordion expand-
    // collapse that grows scrollHeight WITHOUT changing the child list. transitionend →
    // catch the end of an animated expand (max-height, etc.) so the thumb settles.
    var mo = new MutationObserver(scheduleRefresh);
    mo.observe(scrollEl, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['open', 'aria-expanded', 'hidden']
    });
    // Only the END of a height-changing transition (accordion grid-template-rows
    // expand, etc.) needs a re-measure — ignore bubbling hover opacity/colour/
    // transform churn from cards inside the body.
    function onTransitionEnd(e) {
      if (/height|grid-template-rows|block-size/.test(e.propertyName)) scheduleRefresh();
    }
    scrollEl.addEventListener('transitionend', onTransitionEnd, { passive: true });

    applySide();
    if (lgMq) lgMq.addEventListener('change', applySide);

    registry.set(scrollEl, {
      teardown: function () {
        if (rafId) cancelAnimationFrame(rafId);
        if (ro) ro.disconnect(); else window.removeEventListener('resize', scheduleRefresh);
        mo.disconnect();
        scrollEl.removeEventListener('scroll', update);
        scrollEl.removeEventListener('transitionend', onTransitionEnd);
        if (lgMq) lgMq.removeEventListener('change', applySide);
        coarseMq.removeEventListener('change', applyPointerMode);
        if (addedTabindex) scrollEl.removeAttribute('tabindex');
        if (bar.parentNode) bar.parentNode.removeChild(bar);
        registry.delete(scrollEl);
      }
    });
    refresh();
  }

  // Drawers hide by `translate` (still laid out, off-screen), by `hidden`/`display:none` (no box at
  // all), or by an inner opacity-0 "content fade" wrapper (a real, in-viewport box) — and IO/RO
  // geometry can't be trusted to track any of it: IntersectionObserver's isIntersecting/ratio is a
  // documented cross-browser inconsistency for fixed-position elements moved by the standalone CSS
  // `translate` property specifically (github.com/w3c/IntersectionObserver#484) — verified here: it
  // fires exactly once, immediately, reporting isIntersecting:true/ratio:0 for an off-screen closed
  // drawer, then never fires again once it actually opens.
  //
  // So the open-state attributes are the signal instead — the same set drawer-coordinator.js keys
  // on — re-checking real geometry only when one of them actually flips. A closed drawer is also
  // `inert`, which the browser refuses to deliver pointer/focus/scroll events to at all, so any of
  // those firing on the scroll body is a second, instant, guaranteed-correct "it's open" signal,
  // independent of which attribute or how many ancestors up drive that particular drawer.
  var pendingLazy = new Map(); // el -> cleanup()

  var OPEN_ATTRS = [
    'data-mini-bag-open',
    'data-wishlist-drawer-open',
    'data-search-drawer-open',
    'data-menu-open',
    'data-size-guide-open',
    'data-plp-filters-drawer-open',
  ];

  function isReallyVisible(el) {
    if (el.getClientRects().length === 0) return false; // display:none / hidden
    var r = el.getBoundingClientRect();
    var inViewport = r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight;
    return inViewport && getComputedStyle(el.parentNode).opacity !== '0';
  }

  function checkAllPending() {
    pendingLazy.forEach(function (_, el) {
      // A Section API re-render can replaceWith() a pending body before it was ever opened.
      if (!el.isConnected) { stopWatching(el, false); return; }
      if (isReallyVisible(el)) stopWatching(el, true);
    });
  }

  // The open attribute flips before the drawer finishes its transition, so re-check on the far side
  // of it as well — the geometry read is only meaningful once the element has actually moved.
  var openStateObserver = new MutationObserver(function () {
    checkAllPending();
    setTimeout(checkAllPending, 350);
  });
  openStateObserver.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: OPEN_ATTRS,
  });

  function lazyBuild(el) {
    if (registry.has(el) || pendingLazy.has(el)) return; // already bound or already watching
    if (isReallyVisible(el)) { build(el); return; }      // already open (editor re-render, deep link)
    function finish() { stopWatching(el, true); }
    ['pointerdown', 'focusin', 'scroll'].forEach(function (ev) { el.addEventListener(ev, finish, { passive: true }); });
    pendingLazy.set(el, function () {
      ['pointerdown', 'focusin', 'scroll'].forEach(function (ev) { el.removeEventListener(ev, finish); });
    });
  }

  function stopWatching(el, andBuild) {
    var cleanup = pendingLazy.get(el);
    if (!cleanup) return;
    cleanup();
    pendingLazy.delete(el);
    if (andBuild) build(el);
  }

  function bindAll(root) {
    (root || document).querySelectorAll('[data-drawer-scroll]').forEach(lazyBuild);
  }

  function bindOneTrust(root) {
    var content = root && root.matches && root.matches('#onetrust-pc-sdk #ot-pc-content')
      ? root
      : (root || document).querySelector && (root || document).querySelector('#onetrust-pc-sdk #ot-pc-content');
    if (!content) return;
    content.setAttribute('data-drawer-scroll', '');
    lazyBuild(content);
  }

  // Initial bind + section lifecycle (mini-bag & plp are sections; search is global).
  if (document.readyState !== 'loading') { bindAll(); bindOneTrust(document); }
  else document.addEventListener('DOMContentLoaded', function () {
    bindAll();
    bindOneTrust(document);
  }, { once: true });
  document.addEventListener('shopify:section:load', function (e) { bindAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    registry.forEach(function (entry, el) { if (e.target.contains(el)) entry.teardown(); });
    pendingLazy.forEach(function (_, el) { if (e.target.contains(el)) stopWatching(el); });
  });

  // OneTrust and other third-party surfaces can insert their scroll body after both
  // DOMContentLoaded and Shopify's section lifecycle. Bind newly added data-drawer-scroll
  // elements regardless of script/render order.
  var dynamicBindObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === 'attributes') {
        lazyBuild(mutation.target);
        return;
      }
      mutation.addedNodes.forEach(function (node) {
        if (!node || node.nodeType !== 1) return;
        if (node.matches('[data-drawer-scroll]')) lazyBuild(node);
        bindAll(node);
        bindOneTrust(node);
      });
    });
  });
  dynamicBindObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-drawer-scroll']
  });

  // The mini-bag re-renders via the Section API (refreshCart) on every cart change, replaceWith()-swapping
  // its [data-mini-bag-products] (a [data-drawer-scroll] element) — that orphans the old bar and leaves the
  // fresh scroll body unbound (no shopify:section:load fires). Reconcile on mini-bag:updated: teardown any
  // now-detached entries, then bind the new scroll body (bindAll is idempotent for still-attached ones).
  document.addEventListener('mini-bag:updated', function () {
    var stale = [];
    registry.forEach(function (entry, el) { if (!document.contains(el)) stale.push(entry); });
    stale.forEach(function (entry) { entry.teardown(); });
    // A body swapped out before it was ever opened is still being watched — drop it, or its
    // listeners and Map entry outlive the node on every cart change.
    var detached = [];
    pendingLazy.forEach(function (_, el) { if (!el.isConnected) detached.push(el); });
    detached.forEach(function (el) { stopWatching(el, false); });
    bindAll();
    // The bar for the fresh body would otherwise wait for the next open-state flip.
    checkAllPending();
  });

  window.BroraDrawerScrollbar = {
    bind: build,
    bindAll: bindAll,
    refresh: function (el) {
      var entry = registry.get(el);
      if (!entry) return; // re-place/re-measure runs via observers; explicit refresh is a no-op hook
    }
  };
})();
